import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Home, 
  Package, 
  Code, 
  Settings, 
  Upload, 
  Download,
  Database,
  X 
} from 'lucide-react';

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'IR Models', href: '/models', icon: Package },
    // { name: 'IR Codes', href: '/codes', icon: Code }, // Hidden
    { name: 'Import/Export', href: '/import-export', icon: Upload },
  ];

  const adminNavigation = [
    { name: 'Admin Panel', href: '/admin', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ item }) => (
    <Link
      to={item.href}
      onClick={() => setSidebarOpen(false)}
      className={`
        group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
        ${isActive(item.href)
          ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-600'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }
      `}
    >
      <item.icon
        className={`
          mr-3 flex-shrink-0 h-5 w-5 transition-colors duration-200
          ${isActive(item.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
        `}
        aria-hidden="true"
      />
      {item.name}
    </Link>
  );

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 flex z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" aria-hidden="true" />
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 flex flex-col w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out z-50
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:inset-0
        `}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between flex-shrink-0 px-4 py-4 border-b border-gray-200 lg:hidden">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-600 rounded-lg p-2">
              <Database className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">
                IRDB Management
              </h1>
              <p className="text-xs text-gray-500">
                Infrared Database System
              </p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <nav className="flex-1 px-2 py-4 space-y-1">
            {/* Main navigation */}
            <div className="space-y-1">
              {navigation.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
            </div>

            {/* Admin navigation */}
            {user?.role === 'admin' && (
              <>
                <div className="border-t border-gray-200 my-4"></div>
                <div className="space-y-1">
                  <div className="px-2 py-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Administration
                    </h3>
                  </div>
                  {adminNavigation.map((item) => (
                    <NavLink key={item.name} item={item} />
                  ))}
                </div>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200">
            <div className="flex items-center">
              <div className="bg-gray-100 rounded-full p-2">
                <Database className="h-4 w-4 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-900">
                  IR Database Management System
                </p>
                <p className="text-xs text-gray-500">
                  Version 1.0.0
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
